/**
 * Utilitários de formatação compartilhados
 *
 * Este arquivo contém funções de formatação padronizadas para uso
 * em toda a aplicação, evitando duplicação de código.
 */

/**
 * Formata tempo relativo (ex: "Agora", "5 min", "2h", "Ontem")
 *
 * Uso: exibir quando uma conversa/item foi atualizado
 *
 * @param dateString - Data em formato ISO string
 * @returns String formatada com tempo relativo em português
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Agora';
  if (diffMins < 60) return `${diffMins} min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

/**
 * Formata tempo relativo com prefixo "Atualizado"
 *
 * Uso: exibir status de atualização em cards de projeto
 *
 * @param dateString - Data em formato ISO string
 * @returns String formatada com prefixo "Atualizado" em português
 */
export const formatRelativeTimeWithPrefix = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) return 'Atualizado agora';
  if (diffHours < 24) return `Atualizado há ${diffHours}h`;
  if (diffDays === 1) return 'Atualizado ontem';
  if (diffDays < 7) return `Atualizado há ${diffDays} dias`;
  if (diffDays < 30) return `Atualizado há ${Math.floor(diffDays / 7)} semana${diffDays >= 14 ? 's' : ''}`;
  return `Atualizado em ${date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}`;
};

/**
 * Formata tamanho de arquivo em bytes para formato legível
 *
 * Uso: exibir tamanho de arquivos anexados
 *
 * @param bytes - Tamanho em bytes
 * @returns String formatada (ex: "1.5 MB", "500 KB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Formata data completa (ex: "15 dez. 2024")
 *
 * Uso: exibir data de criação de arquivos
 *
 * @param dateString - Data em formato ISO string
 * @returns String formatada com data completa em português
 */
export const formatFullDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Extrai e formata extensão de arquivo
 *
 * Uso: exibir tipo de arquivo em cards
 *
 * @param fileName - Nome do arquivo com extensão
 * @returns Extensão em maiúsculas (ex: "PDF", "DOCX") ou "FILE" se não houver
 */
export const getFileExtension = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension ? extension.toUpperCase() : 'FILE';
};
